/* eslint-disable import/prefer-default-export */
import React, { PropsWithChildren, useContext, useEffect, useRef, useState } from 'react';
import noop from 'lodash-es/noop';

import { DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS } from 'Utilities/analytics/analyticsDocumentCapture';
import { getOrdinal } from 'Utilities/formatter';

import { DOCUMENT_ERROR_TRIGGER, DOCUMENT_UPLOAD_TYPE, generateUploadDocumentAnalyticsEventString } from '../../DocumentCapture.helpers';
import type { DocumentKindsValue } from '../../DocumentCapture.types';
import { DocumentCaptureConfirmationProgressContext } from '../../DocumentCaptureContexts';
import type { Input, StateSections } from '../pages/uploadImageReducer.types';
import { UploadDocType } from '../UploadDocumentsPage.types';
import { UploadDocumentSections } from '../UploadDocumentsSections';

import {
  calculateIsProgressStateSyncNeeded,
  doAllInputsContainUrls,
  doInputsContainBlurryImages,
  doInputsContainReuploadRequests,
  doSomeInputsContainUrls,
  getImagesFromInputs,
  getMissingOrNextPageNumber,
} from './useImageManager.helpers';
import {
  AddImageParams,
  ImageManagerContextValue,
  RemoveImageParams,
} from './useImageManager.types';

const initialState: ImageManagerContextValue = {
  addExtraPage: noop,
  addImage: noop,
  hasBlurryImages: false,
  hasRequestedReuploadActions: false,
  hasRequiredUploads: false,
  hasUploadedDocuments: false,
  isReentryFlow: false,
  isSubmittingBlocked: true,
  kind: undefined,
  removeImage: noop,
  sections: {
    addAnotherPage: false,
    analytics: { cta: '', guidePages: [''], homePage: '' },
    inputs: [],
    route: { name: '' },
  },
  setShowErrorBox: noop,
  showErrorBox: '',
  uploadDocType: DOCUMENT_UPLOAD_TYPE.DEFAULT,
};

export const ImageManagerContext = React.createContext<ImageManagerContextValue>(initialState);

export const ImageManagerContextProvider = ({
  children,
  initialSections,
  kind,
  reactivated,
  uploadDocType = DOCUMENT_UPLOAD_TYPE.DEFAULT,
}: PropsWithChildren<{
  initialSections: StateSections;
  kind: DocumentKindsValue;
  reactivated: boolean;
  uploadDocType: UploadDocType;
}>) => {
  const [sections, setSections] = useState<StateSections>(initialSections);
  const [showErrorBox, setShowErrorBox] = useState<string | undefined>();

  const { progress, setProgress } = useContext(DocumentCaptureConfirmationProgressContext);

  const { inputs } = sections;

  const previousInputs = useRef(inputs);

  const hasRequestedReuploadActions = doInputsContainReuploadRequests(inputs);
  const hasBlurryImages = doInputsContainBlurryImages(inputs);
  const hasUploadedDocuments = doSomeInputsContainUrls(inputs);
  const hasRequiredUploads = sections.addAnotherPage ? hasUploadedDocuments : doAllInputsContainUrls(inputs);

  const needsAttention = hasBlurryImages || hasRequestedReuploadActions;

  useEffect(() => {
    if (calculateIsProgressStateSyncNeeded(previousInputs.current, inputs)) {
      const updatedDocuments = [...(progress?.documents ?? [])];
      const documentIndexToUpdate = updatedDocuments.findIndex((doc) => doc.kind === kind);

      const updatedDocument = {
        ...updatedDocuments[documentIndexToUpdate],
        images: getImagesFromInputs(inputs).filter((input) => input.documentId),
      };

      updatedDocuments[documentIndexToUpdate] = updatedDocument;

      setProgress?.((prevProgress) => ({
        ...prevProgress,
        documents: updatedDocuments,
      }));
    }
    // Added check here to handle on change
    if (needsAttention) {
      setShowErrorBox(DOCUMENT_ERROR_TRIGGER.ON_CHANGE);
    } else {
      setShowErrorBox(undefined);
    }

    previousInputs.current = inputs;
  }, [inputs, kind, needsAttention, progress?.documents, setProgress]);

  // For old flow - remove once DRFT is ready
  const isSubmittingBlocked = hasRequestedReuploadActions
    || hasBlurryImages
    || !hasRequiredUploads;

  const addExtraPage = () => {
    const pageNumber = getMissingOrNextPageNumber(inputs);

    const label = `${getOrdinal(pageNumber)} page`;
    const name = `${sections.inputs[0].name}_${sections.inputs.length + 1}`;

    const newInputs = [
      ...sections.inputs,
      {
        kind,
        label,
        name,
        pageNumber,
      },
    ];

    setSections({ ...sections, inputs: newInputs });
  };

  const addImage = ({
    documentId,
    isSharp,
    pageNumber,
    sharpness,
    url,
  }: AddImageParams) => {
    const inputIndex = sections.inputs.findIndex(
      (input) => input.pageNumber === pageNumber,
    );

    const inputToUpdate = sections.inputs[inputIndex];

    if (!url || !inputToUpdate) {
      return;
    }

    DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS.DOCUMENTS_UPLOAD_DOCUMENTS_ADDED({
      label: generateUploadDocumentAnalyticsEventString({
        hasRequestedReuploadActions,
        message: inputToUpdate?.name,
        uploadDocType,
      }),
    });

    const updatedInput: Input = {
      ...inputToUpdate,
      actions: {},
      documentId,
      isSharp,
      sharpness,
      url,
    };

    setSections((prevSections) => {
      const newInputs = [...prevSections.inputs];
      newInputs[inputIndex] = updatedInput;
      return { ...prevSections, inputs: newInputs };
    });
  };

  const removeImage = ({ documentId }: RemoveImageParams) => {
    setSections((prevSections) => {
      const index = prevSections.inputs.findIndex((input) => input.documentId === documentId);

      if (index === -1) {
        return prevSections;
      }

      const inputToBeRemoved = prevSections.inputs[index];

      const placeholderInputsForDocumentKind = UploadDocumentSections[kind].inputs.map((input) => ({ ...input }));
      const placeholderInputForPageBeingRemoved = placeholderInputsForDocumentKind.find(
        (input) => input.pageNumber === inputToBeRemoved.pageNumber,
      );

      const newInputs = [...prevSections.inputs];

      // Stops first page from ever getting deleted and maintains placeholder pages for docs like V5.
      if (placeholderInputForPageBeingRemoved) {
        newInputs[index] = {
          ...placeholderInputForPageBeingRemoved,
        };
      } else {
        newInputs.splice(index, 1);
      }

      return {
        ...prevSections,
        inputs: newInputs,
      };
    });
  };

  return (
    <ImageManagerContext.Provider
      value={{
        addExtraPage,
        addImage,
        hasBlurryImages,
        hasRequestedReuploadActions,
        hasRequiredUploads,
        hasUploadedDocuments,
        isReentryFlow: Boolean(reactivated),
        // Possibly redundant now if we use hasRequestedReuploadActions?
        isSubmittingBlocked,
        kind,
        removeImage,
        sections,
        setShowErrorBox,
        showErrorBox,
        uploadDocType,
      }}
    >
      {children}
    </ImageManagerContext.Provider>
  );
};

export const useImageManager = () => useContext(ImageManagerContext);
